/*
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ENGINEERING NOTES
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        1) Do not import the following modules into this module:
            import {
                < any enumeration >
            } from '/ezlibrary/enums/EzEnumerations.js';
            import {
                < any helper classes >
            } from '/ezlibrary/helpers/EzHelpers.js';
            import {
                < any other exception classes >
            } from '/ezlibrary/exceptions/EzExceptions.js';
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
*/

/**
 * @class
 * @extends {Error}
 * @description
 * Top level exception for ezClocker website
 * EzException is the base Exception class for all ezClocker exceptions.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * ENGINEERING NOTES:
 *     1) Do not import this class into /ezlibrary/ezclocker-classes/EzClasses.js
 *        import group module because EzException is alredy exported by the
 *        /ezlibrary/exceptions/EzExceptions.js import group module.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import {
 *         EzException,
 *         // EzExceptionInClassMethod,
 *         // EzBadParamException,
 *         // EzBadStateException,
 *         // EzStaticClassException,
 *         // EzNotSupportedException
 *     } from '/ezlibrary/exceptions/EzExceptions.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzException extends Error {
    /**
     * @static
     * @public @readonly @property
     * Gets the default EzException error mesage: 'An unexpected error occurred.'
     * @returns {string}
     */
    static get DEFAULT_ERROR_MESSAGE() {
        return 'An unexpected error occurred.';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the source file name not provided constant string: '(source file name not provided)'
     * @returns {string}
     */
    static get sConst_soruceFileNameNotProvided() {
        return '(source file name not provided)';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the source line number not provided constant string: '(source line number not provided)'
     * @returns {string}
     */
    static get sConst_soruceLineNumberNotProvided() {
        return '(source line number not provided)';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default stack trace for an EzException
     * @returns {string}
     */
    static get oConst_defaultStackTrace() {
        return new Error(EzException.DEFAULT_ERROR_MESSAGE).stack;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default error code constant: 500
     */
    static get DEFAULT_ERROR_CODE() {
        return 500;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default exception options object:
     *  {
     *      name: null,
     *      errorCode: null,
     *      message: null,
     *      causedBy: null,
     *      inSourceFileName: null,
     *      atSourceLineNumber: null,
     *      stack: null
     *  }
     * @type {object}
     */
    static get EMPTY_EXCEPTION_OPTIONS() {
        return {
            name: null,
            errorCode: null,
            message: null,
            causedBy: null,
            inSourceFileName: null,
            atSourceLineNumber: null,
            stack: null
        };
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default exception options object:
     *  {
     *      name: {string: EzClockerException.constructor.name|'EzException'},
     *      errorCode: {number: 500},
     *      message: {string: EzException.DEFAULT_ERROR_MESSAGE),
     *      causedBy: {object: null},
     *      inSourceFileName: {string: EzException.sConst_soruceFileNameNotProvided},
     *      atSourceLineNumber: {number|string: EzException.sConst_soruceFileNameNotProvided},
     *      stack: {object: EzException.oConst_defaultStackTrace}
     *  }
     * @type {object}
     */
    static get DEFAULT_EXCEPTION_OPTIONS() {
        return {
            name: 'EzException',
            errorCode: EzException?.constructor?.name && 'string' === typeof EzException?.constructor?.name
                ? EzException?.constructor?.name
                : 'EzException',
            message: EzException.DEFAULT_ERROR_MESSAGE,
            causedBy: null,
            inSourceFileName: EzException.sConst_soruceFileNameNotProvided,
            atSourceLineNumber: EzException.sConst_soruceFileNameNotProvided,
            stack: EzException.oConst_defaultStackTrace
        };
    }

    /**
     * @static
     * @public @method
     * Builds the default options object to pass to an exception:
     *  {
     *      cause: exceptionInstance?.cause || undefined,
     *      name: exceptionInstance?.constructor.name || 'EzClockerException',
     *      fileName: exceptionInstance?.fileName || undefined,
     *      lineNumber: exceptionInstance?.lineNumber || undefined,
     *      stack: exceptionInstance?.stack || undefined
     *  };
     * @param {null|object} [exceptionInstance=null]
     * @returns {object}
     */
    static ezBuildDefaultOptions(exceptionInstance = null) {
        if (!exceptionInstance) {
            return EzException.DEFAULT_EXCEPTION_OPTIONS;
        }

        if (!exceptionInstance?.stack) {
            try {
                Error?.captureStackTrace(exceptionInstance, exceptionInstance?.constructor);
            } catch (err) {
                globalThis?.console?.warn(
                    'Failed to load the stack trace using Error?.captureStackTrace(exceptionInstance, exceptionInstance?.constructor) ' +
                    'due to the following error: ' + (err?.message || 'Unknown error while loading stack trace.'),
                    err);

                exceptionInstance.stack = EzException.oConst_defaultStackTrace;
            }
        }

        return {
            cause: exceptionInstance?.cause || null,
            name: exceptionInstance?.constructor?.name || EzException?.name || 'EzException',
            fileName: exceptionInstance?.fileName || EzException.sConst_soruceFileNameNotProvided,
            lineNumber: exceptionInstance?.lineNumber || EzException.sConst_soruceLineNumberNotProvided,
            stack: exceptionInstance.stack
        };
    }

    /**
     * @public @constructor     * Creates a new instance of EzException
     * @param {undefined|null|string} message
     * Default is EzException.DEFAULT_ERROR_MESSAGE
     * @param {undefined|null|object|any} exOptions
     * Default is: null
     */
    constructor(message = EzException.DEFAULT_ERROR_MESSAGE, fileNameOrOptions = EzException.DEFAULT_EXCEPTION_OPTIONS, lineNumber = undefined) {
        super(
            // Pass along the message, the message in the options, or use the default
            'string' === typeof message && 0 < message?.length
                ? message
                : ('string' !== typeof fileNameOrOptions &&
                    'string' === typeof fileNameOrOptions?.message &&
                    0 < fileNameOrOptions?.message?.length
                    ? exOptions.message
                    : EzException.DEFAULT_ERROR_MESSAGE),
            // Pass along the 'options' or the file name if a string was provided
            // If not provided: Set empty string if the lineNumber is provided, otherwise pass the default options.
            'string' === typeof fileNameOrOptions
                ? (0 < fileNameOrOptions.length
                    ? fileNameOrOptions
                    : EzException.sConst_soruceFileNameNotProvided)
                : fileNameOrOptions || (!lineNumber
                    ? EzException.DEFAULT_EXCEPTION_OPTIONS
                    : ''),
            // Pass along the provided line number as a value or null/undefined.
            lineNumber);

        const typeofFileNameOrOptions = typeof fileNameOrOptions;

        if ('string' !== typeofFileNameOrOptions && 'object' === typeofFileNameOrOptions) {
            this.exOptions = fileNameOrOptions;
        } else {
            this.exOptions = EzException.EMPTY_EXCEPTION_OPTIONS;

            if ('string' === typeof fileNameOrOptions && 0 < fileNameOrOptions?.length) {
                this.exOptions.fileName = fileNameOrOptions;
            }

            this.lineNumber = exOptions?.atLineNumber
                ? exOptions.atLineNumber
                : undefined;

            this.stack = exOptions?.stack
                ? exOptions.stack
                : EzException.oConst_defaultStackTrace;

            this.name = 'string' == typeof exOptions?.name || 0 < exOptions?.name?.length
                ? exOptions.name
                : this?.constructor?.name | 'EzException';

            this.errorCode = exOptions?.errorCode
                ? exOptions.errorCode
                : EzException.DEFAULT_ERROR_CODE;
        }
    }

    /**
     * @private @field
     * Stores the exception's error code
     * Default is: 500
     * @type {number}
     */
    #errorCode = EzException.DEFAULT_ERROR_CODE;
    /**
     * @public @property @getter
     * Gets the exception's error code
     * @returns {number}
     */
    get errorCode() {
        if (!this.#errorCode) {
            this.#errorCode = this.exOptions?.errorCode || EzException.DEFAULT_ERROR_CODE;
        }

        if (this.#errorCode && !this.exOptions?.errorCode) {
            this.#exOptions.errorCode = this.#errorCode;
        }

        return this.#errorCode;
    }
    /**
     * @public @property @setter
     * Sets the exception's error code
     * @param {number}
     * Default: EzException.DEFAULT_ERROR_CODE
     */
    set errorCode(errorCode) {
        this.#errorCode = errorCode || this.exOptions?.errorCode || EzException.DEFAULT_ERROR_CODE;

        this.#exOptions.errorCode = this.errorCode;
    }

    /**
     * @public @property @getter
     * Gets the message for this exception
     * @returns {string}
     */
    get message() {
        const messageResult = super.message;

        if (!messageResult) {
            messageResult = this.exOptions?.message || EzException.DEFAULT_ERROR_MESSAGE;

            this.message = messageResult;
        }

        if (messageResult && !this.exOptions?.message) {
            this.exOptions.message = messageResult;
        }

        return super.message;
    }
    /**
     * @public @property @setter
     * Sets the message for this exception
     * @param {string} aMessage
     */
    set message(aMessage) {
        super.message = aMessage || this.exOptions?.message || EzException.DEFAULT_ERROR_MESSAGE;

        this.#exOptions.message = this.message;
    }

    #causedBy = null;
    /**
     * @public @property @getter
     * Gets the cause of this exception (a caught Error)
     * @param {null|object}
     */
    get causedBy() {
        if (null == this.#causedBy) {
            this.#causedBy = this.exOptions?.causeBy || this.cause || null;

            if (this.#causedBy && !this.exOptions?.causeBy) {
                this.#exOptions.causeBy = this.#causedBy;
            }
        }

        return this.#causedBy;
    }
    /**
     * @public @property @setter
     * Sets the cause of this exception (a caught Error)
     * @param {undefined|null|object} cause
     */
    set causedBy(causedBy) {
        this.#causedBy = causedBy || this.exOptions?.causedBy || this.cause || null;

        if (this.#causedBy && !this.exOptions?.causeBy) {
            this.#exOptions.causeBy = this.#causedBy;
        }
    }

    /**
     * @public @property @getter
     * Gets the stack trace for the error
     * @returns {string}
     */
    get stack() {
        let stackValue = super.stack;

        if (!stackValue && this.ezOptions?.stack && 0 < this.ezOptions.stack?.length) {
            stackValue = this.exOptions.stack;
        }

        if (!stackValue || 0 > stackValue?.length) {
            const tempError = new Error(this.message);

            Error?.captureStackTrace(tempError, tempError.constructor);

            stackValue = tempError.stack || EzException.oConst_defaultStackTrace;
        }

        if (!stackValue || 0 > stackValue?.length) {
            stackValue = EzException.oConst_defaultStackTrace;
        }

        if (stackValue && !this.exOptions?.stack) {
            this.exOptions.stack = stackValue;
        }

        return stackValue;
    }
    /**
     * @public @property @setter
     * Sets the message for this exception
     * @param {string} stack
     */
    set stack(aStack) {
        if (!aStack || 0 > aStack?.length) {
            this.ezLoadStackTrace();
        } else {
            let stackValue = aStack || this.exOptions?.stack;

            if (!stackValue || 0 > stackValue?.length) {
                stackValue = EzException.oConst_defaultStackTrace;
            }

            if (stackValue && !this.exOptions?.stack) {
                this.exOptions.stack = stackValue;
            }

            super.stack = stackValue;
        }
    }

    /**
     * @public @readonly @property
     * Sets the stack property equal to the stack trace information on this class and then returns the value of the stack property.
     * @returns {string}
     */
    ezLoadStackTrace() {
        try {
            Error?.captureStackTrace(this, this.constructor);
        } catch (err) {
            globalThis?.console?.warn(
                'Failed to load the stack trace using Error?.captureStackTrace(this, this.constructor) due to the following error: ' +
                err?.message || 'Unknown error while loading stack trace.', err);

            this.stack = EzException.oConst_defaultStackTrace;
        }
    }

    /**
     * @private @field
     * Stores the options provided to the exception
     * Default: EzException.DEFAULT_EXCEPTION_OPTIONS
     * @type {object}
     */
    #exOptions = EzException.DEFAULT_EXCEPTION_OPTIONS;
    /**
     * @public @property @getter
     * @type {object}
     */
    get exOptions() {
        if (!this.#exOptions) {
            this.#exOptions = EzException.buildezBuildDefaultOptions(this);
        }

        return this.#exOptions;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {undefined|null|object} options}
     * Default: EzException.DEFAULT_EXCEPTION_OPTIONS
     */
    set exOptions(options = EzException.DEFAULT_EXCEPTION_OPTIONS) {
        if (!options) {
            options = EzException.EMPTY_EXCEPTION_OPTIONS;
        }

        this.#exOptions = EzException.EMPTY_EXCEPTION_OPTIONS;

        this.#exOptions.name = options?.name || null;

        this.#exOptions.message = options?.message || null;

        this.#exOptions.errorCode = options?.errorCode || null;

        this.#exOptions.inSourceFileName = options?.inSourceFileName || options?.fileName || null;

        this.#exOptions.fileName = options?.fileName || options?.inSourceFileName || null;

        this.#exOptions.atSourceLineNumber = options?.atSourceLineNumber || options?.lineNumber || null;

        this.#exOptions.lineNumber = options?.lineNumber || options?.atSourceLineNumber || null;

        this.#exOptions.cause = options?.cause || options?.causedBy || null;

        this.#exOptions.causedBy = options?.causeBy || options?.cause || null;

        this.#exOptions.stack = options?.stack || null;

        this.#applyEzOptionsToProperties()


    }
    /**
     * @public @property @getter
     * Gets the exception options object
     * @returns {object}
     * @deprecated
     * Migrate to class property: EzException.exOptions
     */
    get options() {
        return this.exOptions;
    }
    /**
     * @public @property @setter
     * Sets the exception options object
     * @param {undefined|null|object} exOptions
     * @deprecated
     * Migrate to class property: EzException.exOptions
     */
    set options(exOptions = EzException.DEFAULT_EXCEPTION_OPTIONS) {
        this.exOptions(exOptions);
    }

    /**
     * @private @function
     * Applies the EzOptions's properties to the exception's properties (if provided in the options object)
     */
    #applyEzOptionsToProperties() {
        if (!this.#exOptions) {
            // Nothing to apply
            return;
        }

        if ('string' == typeof this.#exOptions?.name && 0 < this.#exOptions.name?.length) {
            this.name = this.#exOptions.name;
        }

        // Finally, update the exception properties to equal what was provided from the exOptions (if any)
        if ('string' === typeof this.#exOptions?.message && 0 < this.#exOptions.message?.length) {
            this.message = this.#exOptions.message;
        }

        const errorCodeNumberProvided = 'number' === typeof this.#exOptions?.errorCode;

        const errorCodeStringProvided = 'string' === typeof this.#exOptions?.errorCode && 0 < this.#exOptions.errorCode?.length;

        if (errorCodeNumberProvided || errorCodeStringProvided) {
            this.errorCode = errorCodeNumberProvided
                ? this.#exOptions.errorCode
                : Number.parseInt(this.#exOptions.errorCode);
        }

        const inSourceFileNameProvided = 'string' === typeof this.#exOptions?.inSourceFileName && 0 < this.#exOptions.inSourceFileName?.length;

        const fileNameProvided = 'string' === typeof this.#exOptions?.fileName && 0 < this.#exOptions.fileName?.length;

        if (inSourceFileNameProvided || fileNameProvided) {
            this.inSourceFileName = inSourceFileNameProvided
                ? this.#exOptions.inSourceFileName
                : this.exOptions.fileName;

            this.fileName = fileNameProvided
                ? this.#exOptions.fileName
                : this.#exOptions?.inSourceFileName;
        }

        const atSourceLineNumberProvided = 'number' === typeof this.#exOptions?.atSourceLineNumber;

        const atLineNumberProvided = 'number' === typeof this.#exOptions?.atLineNumber;

        const atSourceLineStringProvided = 'string' === typeof this.#exOptions?.atSourceLineNumber && 0 < this.#exOptions.atSourceLineNumber?.length;

        const atLineNumberStringProvided = 'string' === typeof this.#exOptions?.atLineNumber && 0 < this.#exOptions.atLineNumber?.length;

        if (atSourceLineNumberProvided || atLineNumberProvided) {
            this.atSourceLineNumber = atSourceLineNumberProvided
                ? this.#exOptions.atSourceLineNumber
                : this.#exOptions.lineNumber;
        } else if (atSourceLineStringProvided || atLineNumberStringProvided) {
            this.atSourceLineNumber = atSourceLineStringProvided
                ? Number.parseInt(this.#exOptions.atSourceLineNumber)
                : Number.parseInt(this.#exOptions.atLineNumber);
        }

        if (this.#exOptions?.causedBy || this.#exOptions?.caused) {
            this.causedBy = this.#exOptions.causedBy || this.#exOptions.caused;

            this.cause = this.#exOptions.cause || this.#exOptions.causedBy;
        }

        if (('string' === typeof this.#exOptions?.stack && 0 < this.#exOptions.stack?.length) || 'object' === typeof this.#exOptions?.stack) {
            this.stack = this.#exOptions.stack;
        }
    }

    /**
     * @public @readonly @method
     * Gets this exception as a string
     * Template: `${this.name}: ${this.message} (${this.errorCode}) [Stack: ${this.stack}]`
     * @returns {string}
     */
    get toString() {
        return this.exOptions
            ? `${this.name}: ${this.message} (${this.errorCode}) [Stack: ${this.stack}] [Exception Options: ${JSON.stringify(this.exOptions)}]`
            : `${this.name}: ${this.message} (${this.errorCode}) [Stack: ${this.stack}]`;
    }
}

